import { Point2D, WallElement } from '../types';

/**
 * 检测点是否在墙面线段附近
 * @param point 检测点
 * @param wall 墙面元素
 * @param threshold 阈值（像素）
 * @returns 如果在线段附近，返回 { segmentIndex, position, closestPoint }，否则返回 null
 */
export function getPointOnWallSegment(
  point: Point2D,
  wall: WallElement,
  threshold: number = 20
): { segmentIndex: number; position: number; closestPoint: Point2D } | null {
  if (wall.points.length < 2) return null;

  for (let i = 0; i < wall.points.length - 1; i++) {
    const p1 = wall.points[i];
    const p2 = wall.points[i + 1];

    // 计算点到线段的最近点和距离
    const result = getClosestPointOnSegment(point, p1, p2);
    
    if (result.distance <= threshold) {
      return {
        segmentIndex: i,
        position: result.position,
        closestPoint: result.closestPoint,
      };
    }
  }

  return null;
}

/**
 * 计算点到线段的最近点
 */
function getClosestPointOnSegment(
  point: Point2D,
  p1: Point2D,
  p2: Point2D
): { distance: number; position: number; closestPoint: Point2D } {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    // 线段退化为点
    const distance = Math.sqrt(
      (point.x - p1.x) ** 2 + (point.y - p1.y) ** 2
    );
    return { distance, position: 0, closestPoint: p1 };
  }

  // 计算投影参数 t
  let t = ((point.x - p1.x) * dx + (point.y - p1.y) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t)); // 限制在 [0, 1] 范围内

  // 计算最近点
  const closestPoint = {
    x: p1.x + t * dx,
    y: p1.y + t * dy,
  };

  // 计算距离
  const distance = Math.sqrt(
    (point.x - closestPoint.x) ** 2 + (point.y - closestPoint.y) ** 2
  );

  return { distance, position: t, closestPoint };
}

/**
 * 在所有墙面中查找点击点所在的墙面线段
 */
export function findWallSegmentAtPoint(
  point: Point2D,
  walls: WallElement[],
  threshold: number = 20
): { wall: WallElement; segmentIndex: number; position: number; closestPoint: Point2D } | null {
  for (const wall of walls) {
    const result = getPointOnWallSegment(point, wall, threshold);
    if (result) {
      return {
        wall,
        segmentIndex: result.segmentIndex,
        position: result.position,
        closestPoint: result.closestPoint,
      };
    }
  }
  return null;
}

/**
 * 将点吸附到最近的墙面线段上
 * @param point 原始点
 * @param walls 墙面列表
 * @param threshold 吸附阈值
 * @returns 吸附后的点信息，如果没有找到墙面返回null
 */
export function snapToWall(
  point: Point2D,
  walls: WallElement[],
  threshold: number = 30
): { wallId: string; wallSegmentIndex: number; snappedPoint: Point2D } | null {
  let closestResult: {
    wall: WallElement;
    segmentIndex: number;
    closestPoint: Point2D;
    distance: number;
  } | null = null;
  let minDistance = threshold;

  for (const wall of walls) {
    if (wall.points.length < 2) continue;

    for (let i = 0; i < wall.points.length - 1; i++) {
      const p1 = wall.points[i];
      const p2 = wall.points[i + 1];
      const result = getClosestPointOnSegment(point, p1, p2);

      if (result.distance < minDistance) {
        minDistance = result.distance;
        closestResult = {
          wall,
          segmentIndex: i,
          closestPoint: result.closestPoint,
          distance: result.distance,
        };
      }
    }
  }

  if (closestResult) {
    return {
      wallId: closestResult.wall.id,
      wallSegmentIndex: closestResult.segmentIndex,
      snappedPoint: closestResult.closestPoint,
    };
  }

  return null;
}

/**
 * 将点限制在墙面线段的范围内
 * @param point 要限制的点
 * @param wallP1 墙面线段起点
 * @param wallP2 墙面线段终点
 * @returns 限制后的点
 */
export function clampPointToSegment(
  point: Point2D,
  wallP1: Point2D,
  wallP2: Point2D
): Point2D {
  const dx = wallP2.x - wallP1.x;
  const dy = wallP2.y - wallP1.y;
  const segmentLenSq = dx * dx + dy * dy;
  
  if (segmentLenSq === 0) return wallP1; // 退化为点
  
  // 计算点在线段上的投影参数
  let t = ((point.x - wallP1.x) * dx + (point.y - wallP1.y) * dy) / segmentLenSq;
  t = Math.max(0, Math.min(1, t)); // 限制在 [0, 1] 范围内
  
  return {
    x: wallP1.x + t * dx,
    y: wallP1.y + t * dy,
  };
}
